import React, { createContext, useContext, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';
import { useAuth } from '../auth/AuthContext';

const POLL_INTERVAL_MS = 2 * 60 * 1000;

const SystemNotificationContext = createContext({
  notification: null,
  dismissedId: null,
  dismiss: () => {},
  isVisible: false,
  isFetched: false,
});

// Centralizes the admin-pushed system notification (GET /api/admin/system-
// notification) state so multiple consumers can coordinate:
//   - The SystemNotificationBanner / ClientSystemNotificationBanner render it.
//   - The SW "Update available" banners suppress themselves when this
//     notification is visible, so release notes don't stack with refresh UI.
//
// Dismissal lives here (not in each banner's local state) so the SW banner
// can re-appear once the system banner is dismissed.
export function SystemNotificationProvider({ children, enabled = true }) {
  const { isAuthenticated } = useAuth();
  const notificationsEnabled = enabled && isAuthenticated;
  const [dismissedId, setDismissedId] = useState(null);

  const { data: notification, isFetched } = useQuery({
    queryKey: ['system-notification'],
    queryFn: () => api.getSystemNotification(),
    enabled: notificationsEnabled,
    refetchInterval: POLL_INTERVAL_MS,
    retry: false,
    onError: () => {},
  });

  const dismiss = useCallback((id) => {
    setDismissedId(id);
  }, []);

  const isVisible = Boolean(notificationsEnabled && notification && notification.id !== dismissedId);

  return (
    <SystemNotificationContext.Provider
      value={{ notification: notificationsEnabled ? notification : null, dismissedId, dismiss, isVisible,
        // The update-refreshed dialog must not wait on a deliberately skipped request.
        isFetched: !notificationsEnabled || isFetched }}
    >
      {children}
    </SystemNotificationContext.Provider>
  );
}

export function useSystemNotification() {
  return useContext(SystemNotificationContext);
}
